import { cn } from "@/lib/utils";
import { X } from "lucide-react";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { cleanValue } from "./utils/cleanValue";
import { fixedDecimalValue } from "./utils/fixedDecimalValue";
import { formatValue } from "./utils/formatValue";
import { getLocaleConfig } from "./utils/getLocaleConfig";
import { getSuffix } from "./utils/getSuffix";
import { isNumber } from "./utils/isNumber";
import { padTrimValue } from "./utils/padTrimValue";
import { repositionCursor } from "./utils/repositionCursor";
import {
  EndAdormentContainer,
  EndIconsContainer,
  ErrorMessage,
  HelperText,
  InputAndErrorContainer,
  InputClearIcon,
  InputContainer,
  InputSingleContainer,
  Label,
} from "./Input";

export interface CurrencyInputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  allowDecimals?: boolean;
  allowNegativeValue?: boolean;
  customInput?: React.ElementType;
  /**
   * Al enfocar el campo, selecciona todo su contenido para que tipear lo
   * reemplace. Activado por defecto: los campos de importe se siembran con
   * un valor ya formateado ("2.408,00"), es decir con los decimales al
   * límite, así que un click al final dejaba el campo CONGELADO — cada tecla
   * caía en la ranura de decimales, que `cleanValue` recorta a
   * `decimalsLimit`, y no pasaba nada. Ver el fix de precios personalizados.
   * En un campo vacío no cambia nada (no hay qué seleccionar).
   */
  selectOnFocus?: boolean;
  decimalsLimit?: number;
  decimalScale?: number;
  fixedDecimalLength?: number;
  prefix?: string;
  suffix?: string;
  intlConfig?: {
    locale: string;
    currency?: string;
  };
  step?: number;
  disableGroupSeparators?: boolean;
  disableAbbreviations?: boolean;
  decimalSeparator?: string;
  groupSeparator?: string;
  transformRawValue?: (rawValue: string) => string;
  onValueChange?: (
    value: string,
    name?: string,
    values?: {
      float: number | null;
      formatted: string;
      value: string;
    }
  ) => void;

  // UI Props
  label?: string;
  isRequired?: boolean;
  helperText?: string;
  error?: string[];
  isInvalid?: boolean;
  light?: boolean;
  dark?: boolean;
  clearInput?: () => void;
  hideClearButton?: boolean;
  endAdorment?: React.ReactNode;

  // Style Props
  labelClassname?: string;
  inputIconClassname?: string;
  endAdormentClassname?: string;
  inputClearIconClassname?: string;
  inputClassname?: string;
  helperTextClassname?: string;
}

export const CurrencyInput = forwardRef<HTMLInputElement, CurrencyInputProps>(
  (
    {
      allowDecimals = true,
      allowNegativeValue = true,
      id,
      name,
      className,
      customInput,
      decimalsLimit,
      defaultValue,
      disabled = false,
      maxLength: userMaxLength,
      value: userValue,
      onValueChange,
      fixedDecimalLength,
      placeholder,
      decimalScale,
      prefix,
      suffix,
      intlConfig,
      step,
      min,
      max,
      disableGroupSeparators = false,
      disableAbbreviations = false,
      decimalSeparator: _decimalSeparator,
      groupSeparator: _groupSeparator,
      onChange,
      onFocus,
      onBlur,
      onKeyDown,
      onKeyUp,
      onMouseDown,
      onMouseUp,
      selectOnFocus = true,
      transformRawValue,
      label,
      isRequired,
      helperText,
      error,
      isInvalid,
      clearInput,
      hideClearButton,
      endAdorment,
      labelClassname,
      endAdormentClassname,
      inputClearIconClassname,
      inputClassname,
      helperTextClassname,
      ...props
    },
    ref
  ) => {
    if (_decimalSeparator && isNumber(_decimalSeparator)) {
      throw new Error("decimalSeparator cannot be a number");
    }

    if (_groupSeparator && isNumber(_groupSeparator)) {
      throw new Error("groupSeparator cannot be a number");
    }

    const defaultIntlConfig = useMemo(
      () => intlConfig || { locale: "es-AR", currency: "ARS" },
      [intlConfig]
    );

    const localeConfig = useMemo(
      () => getLocaleConfig(defaultIntlConfig),
      [defaultIntlConfig]
    );

    const decimalSeparator =
      _decimalSeparator || localeConfig.decimalSeparator || "";

    const groupSeparator = _groupSeparator || localeConfig.groupSeparator || "";

    if (
      decimalSeparator &&
      groupSeparator &&
      decimalSeparator === groupSeparator &&
      disableGroupSeparators === false
    ) {
      throw new Error("decimalSeparator cannot be the same as groupSeparator");
    }

    const formatValueOptions = {
      decimalSeparator,
      groupSeparator,
      disableGroupSeparators,
      intlConfig: defaultIntlConfig,
      prefix: prefix || localeConfig.prefix || "$",
      suffix: suffix,
    };

    const cleanValueOptions = {
      decimalSeparator,
      groupSeparator,
      allowDecimals,
      decimalsLimit: decimalsLimit || fixedDecimalLength || 2,
      allowNegativeValue,
      disableAbbreviations,
      prefix: prefix || localeConfig.prefix,
      transformRawValue,
    };

    const formattedStateValue =
      defaultValue !== undefined && defaultValue !== null
        ? formatValue({
            ...formatValueOptions,
            decimalScale,
            value: String(defaultValue),
          })
        : userValue !== undefined && userValue !== null
        ? formatValue({
            ...formatValueOptions,
            decimalScale,
            value: String(userValue),
          })
        : "";

    const [stateValue, setStateValue] = useState(formattedStateValue);
    const [dirty, setDirty] = useState(false);
    const [cursor, setCursor] = useState(0);
    const [changeCount, setChangeCount] = useState(0);
    const [lastKeyStroke, setLastKeyStroke] = useState(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const selectAllOnMouseUp = useRef(false);

    useImperativeHandle(ref, () => inputRef.current as any);

    // Add browser detection
    const isSafari = useMemo(() => {
      const ua = navigator.userAgent.toLowerCase();
      return ua.includes("safari") && !ua.includes("chrome");
    }, []);

    /**
     * Process change in value
     */
    const processChange = (value: any, selectionStart: any) => {
      setDirty(true);

      const { modifiedValue, cursorPosition } = repositionCursor({
        selectionStart,
        value,
        lastKeyStroke,
        stateValue,
        groupSeparator,
      });

      const stringValue = cleanValue({
        value: modifiedValue,
        ...cleanValueOptions,
      });

      if (
        userMaxLength &&
        stringValue.replace(/-/g, "").length > userMaxLength
      ) {
        return;
      }

      if (
        stringValue === "" ||
        stringValue === "-" ||
        stringValue === decimalSeparator
      ) {
        if (onValueChange) {
          onValueChange("", name, {
            float: null,
            formatted: "",
            value: "",
          });
        }
        setStateValue(stringValue);
        // Always sets cursor after '-' or decimalSeparator input
        setCursor(1);
        return;
      }

      const stringValueWithoutSeparator = decimalSeparator
        ? stringValue.replace(decimalSeparator, ".")
        : stringValue;

      const numberValue = parseFloat(stringValueWithoutSeparator);

      const formattedValue = formatValue({
        value: stringValue,
        ...formatValueOptions,
      });

      if (cursorPosition !== undefined && cursorPosition !== null) {
        // Prevent cursor jumping
        let newCursor = cursorPosition + (formattedValue.length - value.length);
        newCursor = newCursor <= 0 ? (prefix ? prefix.length : 0) : newCursor;

        setCursor(newCursor);
        setChangeCount(changeCount + 1);
      }

      setStateValue(formattedValue);

      if (onValueChange) {
        const values = {
          float: numberValue,
          formatted: formattedValue,
          value: stringValue,
        };

        onValueChange(stringValue, name, values);
      }
    };

    /**
     * Handle change event
     */
    const handleOnChange = (event: any) => {
      const {
        target: { value, selectionStart },
      } = event;

      processChange(value, selectionStart);

      if (onChange) {
        onChange(event);
      }
    };

    /**
     * Handle focus event
     */
    const handleOnFocus = (event: any) => {
      // Cubre el foco por teclado (Tab). El foco por click lo resuelve
      // `handleOnMouseUp`, porque el mouseup posterior colapsaría esta
      // selección contra el punto donde se clickeó.
      if (selectOnFocus) {
        event.target.select();
      }

      if (onFocus) {
        onFocus(event);
      }
      return stateValue ? stateValue.length : 0;
    };

    /**
     * Sólo queremos seleccionar todo en el click que ENFOCA el campo. Si ya
     * estaba enfocado, el click sirve para posicionar el cursor y no hay que
     * pisarlo.
     */
    const handleOnMouseDown = (event: any) => {
      selectAllOnMouseUp.current =
        selectOnFocus && document.activeElement !== inputRef.current;

      if (onMouseDown) {
        onMouseDown(event);
      }
    };

    const handleOnMouseUp = (event: any) => {
      if (selectAllOnMouseUp.current) {
        selectAllOnMouseUp.current = false;
        // Sin el preventDefault el mouseup colapsa la selección al caret.
        event.preventDefault();
        inputRef.current?.select();
      }

      if (onMouseUp) {
        onMouseUp(event);
      }
    };

    /**
     * Handle blur event
     *
     * Format value by padding/trimming decimals if required by
     */
    const handleOnBlur = (event: any) => {
      const {
        target: { value },
      } = event;

      const valueOnly = cleanValue({ value, ...cleanValueOptions });

      if (valueOnly === "-" || valueOnly === decimalSeparator || !valueOnly) {
        setStateValue("");
        if (onBlur) {
          onBlur(event);
        }
        return;
      }

      const fixedDecimals = fixedDecimalValue(
        valueOnly,
        decimalSeparator,
        fixedDecimalLength
      );

      const newValue = padTrimValue(
        fixedDecimals,
        decimalSeparator,
        decimalScale !== undefined ? decimalScale : fixedDecimalLength
      );

      const numberValue = parseFloat(newValue.replace(decimalSeparator, "."));

      const formattedValue = formatValue({
        ...formatValueOptions,
        value: newValue,
      });

      if (onValueChange) {
        onValueChange(newValue, name, {
          float: numberValue,
          formatted: formattedValue,
          value: newValue,
        });
      }

      setStateValue(formattedValue);

      if (onBlur) {
        onBlur(event);
      }
    };

    /**
     * Handle key down event
     *
     * Increase or decrease value by step
     */
    const handleOnKeyDown = (event: any) => {
      const { key } = event;

      setLastKeyStroke(key);

      if (step && (key === "ArrowUp" || key === "ArrowDown")) {
        event.preventDefault();
        setCursor(stateValue.length);

        const currentValue =
          parseFloat(
            userValue !== undefined && userValue !== null
              ? String(userValue).replace(decimalSeparator, ".")
              : cleanValue({ value: stateValue, ...cleanValueOptions })
          ) || 0;
        const newValue =
          key === "ArrowUp" ? currentValue + step : currentValue - step;

        if (min !== undefined && newValue < Number(min)) {
          return;
        }

        if (max !== undefined && newValue > Number(max)) {
          return;
        }

        const fixedLength = String(step).includes(".")
          ? Number(String(step).split(".")[1].length)
          : undefined;

        processChange(
          String(
            fixedLength ? newValue.toFixed(fixedLength) : newValue
          ).replace(".", decimalSeparator),
          stateValue.length
        );
      }

      if (onKeyDown) {
        onKeyDown(event);
      }
    };

    /**
     * Handle key up event
     *
     * Move cursor if there is a suffix to prevent user typing past suffix
     */
    const handleOnKeyUp = (event: any) => {
      const {
        key,
        currentTarget: { selectionStart },
      } = event;
      if (key !== "ArrowUp" && key !== "ArrowDown" && stateValue !== "-") {
        const suffix = getSuffix(stateValue, {
          groupSeparator,
          decimalSeparator,
        });

        if (
          suffix &&
          selectionStart &&
          selectionStart > stateValue.length - suffix.length
        ) {
          /* istanbul ignore else */
          if (inputRef.current) {
            const newCursor = stateValue.length - suffix.length;
            inputRef.current.setSelectionRange(newCursor, newCursor);
          }
        }
      }

      if (onKeyUp) {
        onKeyUp(event);
      }
    };

    useEffect(() => {
      // prevent cursor jumping if editing value
      if (
        dirty &&
        stateValue !== "-" &&
        inputRef.current &&
        document.activeElement === inputRef.current
      ) {
        inputRef.current.setSelectionRange(cursor, cursor);
      }
    }, [stateValue, cursor, inputRef, dirty, changeCount]);

    /**
     * If user has only entered "-" or decimal separator,
     * keep the char to allow them to enter next value
     */
    const getRenderValue = () => {
      if (
        userValue !== undefined &&
        userValue !== null &&
        stateValue !== "-" &&
        (!decimalSeparator || stateValue !== decimalSeparator)
      ) {
        return formatValue({
          ...formatValueOptions,
          decimalScale: dirty ? undefined : decimalScale,
          value: String(userValue),
        });
      }

      return stateValue;
    };

    const inputProps = {
      type: "text",
      // Change inputMode for Safari
      inputMode: isSafari ? ("numeric" as const) : ("decimal" as const),
      pattern: isSafari ? "[0-9]*" : undefined,
      id,
      name,
      className,
      onChange: handleOnChange,
      onBlur: handleOnBlur,
      onFocus: handleOnFocus,
      onKeyDown: handleOnKeyDown,
      onKeyUp: handleOnKeyUp,
      onMouseDown: handleOnMouseDown,
      onMouseUp: handleOnMouseUp,
      placeholder,
      disabled,
      value: getRenderValue(),
      ref: inputRef,
      ...props,
    };

    if (customInput) {
      const CustomInput = customInput;
      return <CustomInput {...inputProps} />;
    }

    return (
      <InputContainer className={className} rounded={false}>
        {label && (
          <Label
            labelClassname={labelClassname}
            id={inputProps.id}
            isRequired={isRequired}
          >
            {label}
          </Label>
        )}

        <div className="flex flex-col gap-2 w-full">
          <InputAndErrorContainer>
            <InputSingleContainer>
              {/* <InputIcon
                inputIconClassname={inputIconClassname}
                onClick={() => inputRef.current?.focus()}
              >
                <DollarSign className="w-5 h-5" />
              </InputIcon> */}

              <input
                {...inputProps}
                className={cn(
                  "w-full",
                  "rounded-[10px]",
                  "max-w-full p-2 px-5",
                  "bg-background-100",
                  isInvalid
                    ? "border-error-primary border shadow-error"
                    : "border-border border",
                  "transition-all duration-300",
                  disabled &&
                    "bg-input-bg-disabled text-text-secondary cursor-not-allowed",
                  "focus:outline-hidden focus:ring-1 focus:ring-brand-primary",
                  isInvalid && "focus:ring-error-primary",
                  "placeholder:text-text-secondary",
                  endAdorment || (!hideClearButton && clearInput)
                    ? "pr-16"
                    : "pr-2.5",
                  // Add Safari-specific padding
                  isSafari && "safari-padding",
                  inputClassname
                )}
              />

              <EndIconsContainer>
                {!disabled &&
                  !hideClearButton &&
                  clearInput &&
                  inputProps.value && (
                    <InputClearIcon
                      inputClearIconClassname={inputClearIconClassname}
                      onClick={clearInput}
                    >
                      <X className="w-5 h-5" />
                    </InputClearIcon>
                  )}
                {endAdorment && (
                  <EndAdormentContainer
                    endAdormentClassname={endAdormentClassname}
                  >
                    {endAdorment}
                  </EndAdormentContainer>
                )}
              </EndIconsContainer>
            </InputSingleContainer>
          </InputAndErrorContainer>

          {helperText && (
            <HelperText helperTextClassname={helperTextClassname}>
              {helperText}
            </HelperText>
          )}

          {isInvalid && error && (
            <div className="flex flex-col gap-1">
              {error.map(
                (err, index) =>
                  err && <ErrorMessage key={index}>{err}</ErrorMessage>
              )}
            </div>
          )}
        </div>
      </InputContainer>
    );
  }
);

CurrencyInput.displayName = "CurrencyInput";

export default CurrencyInput;
